import React, { createContext, useCallback, useMemo, useRef, useState, useEffect } from 'react'
import {
  initializeRailgunEngine,
  loadRailgunProviders,
  RAILGUN_DEBUG
} from '@web/utils/railgunCore'
import { FEATURES } from '@web/constants/features'
import { waitForRailgunWorkerEngine } from '../../../mobile-web/services/railgunWorker/railgunWorkerBridge'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useControllerState from '@web/hooks/useControllerState'

export interface AppModeContextValue {
  mode: Mode
  railgunEngineInitialized: boolean
  railgunEngineInitializing: boolean
  railgunEngineInitializingError: boolean
  setMode: (mode: Mode) => void
  /**
   * Idempotently start the Railgun engine. Safe to call repeatedly — only the first call
   * triggers init; subsequent calls are no-ops while initialization runs or once it's done.
   * This is the ONLY entry point that boots the engine: nothing is warmed at unlock, so
   * every privacy entry point calls it — the Private toggle, the Shield / Unshield /
   * private-send screens (`useEnsureRailgunEngineOnPrivateScreen`) and the retry handlers.
   */
  ensureRailgunEngine: () => void
}

export type Mode = 'public' | 'private'

export enum ModeEnum {
  PUBLIC = 'public',
  PRIVATE = 'private'
}

/** Delay before auto-switching to private when requested via URL (after Railgun is ready). */
const PRIVATE_AUTO_SWITCH_DELAY_MS = 250

/** How long to wait for the SW's RailgunBackgroundController to flip `engineInitialized`. */
const OFFSCREEN_ENGINE_READY_TIMEOUT_MS = 5 * 60 * 1000

/** How long to wait for the nested railgun worker to report its engine ready (A1). */
const RAILGUN_WORKER_ENGINE_READY_TIMEOUT_MS = 5 * 60 * 1000

const AppModeContext = createContext<AppModeContextValue>({
  mode: ModeEnum.PUBLIC,
  railgunEngineInitialized: false,
  railgunEngineInitializing: false,
  railgunEngineInitializingError: false,
  setMode: () => {
    throw new Error('setMode not implemented')
  },
  ensureRailgunEngine: () => {
    throw new Error('ensureRailgunEngine not implemented')
  }
})

const AppModeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { dispatch } = useBackgroundService()
  // Subscribe to the SW's RailgunBackgroundController so we learn `engineInitialized` from
  // the same source-of-truth the SW already maintains. This replaces a previous direct
  // UI→offscreen STATUS poll that was racy against SW's offscreen-document creation: from
  // a UI tab, `chrome.runtime.connect({name: 'railgun-offscreen'})` would sometimes connect
  // to a port that never received a response, leaving `STATUS` RPCs to time out for 90s
  // even though the engine was actually ready in the SW. Going through controller-state
  // means we observe the same flag the SW logs as `engine: ready`.
  const railgunBgState = useControllerState('RailgunBackgroundController')
  const swReportsEngineReady = !!(railgunBgState as any)?.engineInitialized
  const [mode, setModeState] = useState<Mode>(ModeEnum.PUBLIC)
  const [requestedModeFromUrl, setRequestedModeFromUrl] = useState<Mode | null>(null)
  const [railgunEngineInitialized, setRailgunEngineInitialized] = useState(false)
  const [railgunEngineInitializing, setRailgunEngineInitializing] = useState(false)
  const [railgunEngineInitializingError, setRailgunEngineInitializingError] =
    useState<boolean>(false)
  const initStartedRef = useRef(false)

  // Reflect the SW controller's engine-ready signal into our local flag. The Private
  // toggle stays enabled once the engine has been seen up — `railgunEngineInitialized`
  // is sticky for that purpose. But when SW idle-stops the engine we also reset
  // `initStartedRef` so the next call to `ensureRailgunEngine` actually dispatches a
  // fresh `ENSURE_OFFSCREEN_ENGINE` instead of short-circuiting against a stale latch.
  // Without this reset, post-idle-stop interactions (toggle Private, gas estimate,
  // refresh balances) never wake the SW back up.
  useEffect(() => {
    if (swReportsEngineReady && !railgunEngineInitialized) {
      setRailgunEngineInitialized(true)
      setRailgunEngineInitializing(false)
      setRailgunEngineInitializingError(false)
    }
    // SW idle-stop recovery only applies when the SW owns the engine (offscreen path).
    // On mobile-as-webapp the engine runs in-page and there is no SW to flip
    // `swReportsEngineReady` true — leaving the reset unconditional caused
    // `initStartedRef` to clear after every successful init, so the next PRIVATE
    // toggle would re-enter `startRailgunEngine` (which is not idempotent) and
    // surface an init error that bounced the UI back to PUBLIC.
    if (
      FEATURES.railgunEngineOffscreenEnabled &&
      !swReportsEngineReady &&
      initStartedRef.current &&
      !railgunEngineInitializing
    ) {
      initStartedRef.current = false
    }
  }, [swReportsEngineReady, railgunEngineInitialized, railgunEngineInitializing])

  useEffect(() => {
    const parseModeFromHash = (): Mode | null => {
      try {
        const hash = window.location.hash ?? ''
        const queryStart = hash.indexOf('?')
        if (queryStart === -1) return null
        const query = hash.slice(queryStart + 1)
        const params = new URLSearchParams(query)
        const rawMode = params.get('mode')
        if (rawMode === ModeEnum.PUBLIC) return ModeEnum.PUBLIC
        if (rawMode === ModeEnum.PRIVATE) return ModeEnum.PRIVATE
        return null
      } catch {
        return null
      }
    }

    const applyFromUrl = () => {
      const parsed = parseModeFromHash()
      setRequestedModeFromUrl(parsed)
      // PUBLIC is applied imperatively right here (not via the effect below) for two
      // reasons: (1) a repeated `mode=public` hash bails out of the state update
      // (same value), so an effect keyed on `requestedModeFromUrl` would not re-fire
      // even though the tab may have been toggled to PRIVATE meanwhile — e.g. the
      // popup re-pinning an already-open full-page tab to the public swap screen
      // (Trello #410); (2) it must not sit in the engine-state effect, whose deps
      // would re-force PUBLIC whenever the engine flips ready/error while a stale
      // `mode=public` is still in the hash.
      if (parsed === ModeEnum.PUBLIC) setModeState(ModeEnum.PUBLIC)
    }

    applyFromUrl()
    window.addEventListener('hashchange', applyFromUrl)
    return () => window.removeEventListener('hashchange', applyFromUrl)
  }, [])

  useEffect(() => {
    // PUBLIC is handled imperatively in `applyFromUrl` above — this effect only
    // drives the deferred PRIVATE switch, which has to wait on engine state.
    if (requestedModeFromUrl !== ModeEnum.PRIVATE) return

    // When mode=private is requested via URL, keep the UI in public mode while Railgun initializes,
    // then switch to private only after successful initialization. If init fails, do not auto-switch.
    if (railgunEngineInitializingError) {
      setModeState(ModeEnum.PUBLIC)
      return
    }

    if (!railgunEngineInitialized) {
      setModeState(ModeEnum.PUBLIC)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setModeState(ModeEnum.PRIVATE)
    }, PRIVATE_AUTO_SWITCH_DELAY_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [requestedModeFromUrl, railgunEngineInitialized, railgunEngineInitializingError])

  // Engine init is deferred — it only fires when the user actually opts into privacy:
  //  - entering private mode via the toggle (or `?mode=private` in the URL), OR
  //  - opening one of the private screens: Shield / Unshield / private send /
  //    unshield-to-origin (`useEnsureRailgunEngineOnPrivateScreen`), OR
  //  - a dapp asking for a railgun account / balance (handled SW-side).
  // Booting costs a merkletree scan and not every user has a private wallet, so unlock
  // stays cold — the SW no longer warms up on `keystore-unlocked` either.
  //
  // Implementation note: in the offscreen-engine path we no longer poll offscreen STATUS RPCs
  // ourselves — we just dispatch `ENSURE_OFFSCREEN_ENGINE` to the SW and watch for the SW's
  // `RailgunBackgroundController.engineInitialized` flag (handled in the effect above). The
  // safety timeout below still guards the case where SW never reports ready (e.g. SW killed
  // mid-bootstrap), so we surface a `failed` state instead of leaving the spinner spinning.
  const swReportsEngineReadyRef = useRef(swReportsEngineReady)
  useEffect(() => {
    swReportsEngineReadyRef.current = swReportsEngineReady
  }, [swReportsEngineReady])

  const ensureRailgunEngine = useCallback(() => {
    // Extension builds: the SW-side RAILGUN work (engine, wallet rows and the
    // per-chain balance snapshots the private UI renders from) is lazy — it no
    // longer runs at unlock. Entering privacy is what kicks it off. Dispatched for
    // the offscreen AND the legacy in-page-engine build, since both read private
    // balances from the SW's `accountSnapshots`, and dispatched BEFORE the latches
    // below: the engine can already be up for an unrelated RPC (the offscreen
    // client boots it on demand) while the SW-side sync has never run.
    // Single-flighted in the controller, so repeat calls are cheap.
    if (!FEATURES.railgunWorkerIsolationEnabled) {
      dispatch({ type: 'RAILGUN_BACKGROUND_CONTROLLER_WARMUP_FOR_PRIVATE' })
    }
    if (initStartedRef.current) return
    if (swReportsEngineReadyRef.current) return // already up — nothing to do
    initStartedRef.current = true

    const init = async () => {
      try {
        // Clear any prior error so a click-to-retry from the Header `failed` state shows
        // the spinner cleanly rather than briefly flashing the error tooltip again.
        setRailgunEngineInitializingError(false)
        setRailgunEngineInitializing(true)
        if (FEATURES.railgunEngineOffscreenEnabled) {
          // SW owns the engine inside the offscreen document. We dispatch the request then
          // wait for the controller-state effect to flip `swReportsEngineReady` true, with
          // a hard ceiling so we surface failure instead of looping forever.
          dispatch({ type: 'RAILGUN_BACKGROUND_CONTROLLER_ENSURE_OFFSCREEN_ENGINE' })
          const startedAt = Date.now()
          while (!swReportsEngineReadyRef.current) {
            if (Date.now() - startedAt > OFFSCREEN_ENGINE_READY_TIMEOUT_MS) {
              throw new Error('SW did not report Railgun engine ready in time')
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 250))
          }
        } else if (FEATURES.railgunWorkerIsolationEnabled) {
          // A1 keystore-isolation (mobile-web): the engine runs in a dedicated
          // worker nested under the keystore worker, not on this page. Ask the
          // worker to boot it (the request is relayed page → keystore worker →
          // nested railgun worker) and wait for the ready signal the worker
          // relays back through `railgunWorkerBridge`.
          // Worker-tier plumbing action, intercepted in `mainController.worker.ts`
          // before controller routing — not part of the typed Action union.
          dispatch({ type: 'RAILGUN_WORKER_ENSURE_ENGINE' } as any)
          await waitForRailgunWorkerEngine(RAILGUN_WORKER_ENGINE_READY_TIMEOUT_MS)
        } else {
          await initializeRailgunEngine({ awaitArtifactPreload: true })
          await loadRailgunProviders()
        }
        setRailgunEngineInitialized(true)
        if (RAILGUN_DEBUG) {
          console.log('Railgun engine initialized')
        }
      } catch (error) {
        console.error('Failed to initialize Railgun engine:', error)
        setRailgunEngineInitializingError(true)
        // Bug-fix: on init failure, allow the next call to retry. Without this
        // reset, a transient failure (e.g. SW asleep at the moment of dispatch)
        // wedges the toggle in a permanent error state for this React mount.
        initStartedRef.current = false
      } finally {
        setRailgunEngineInitializing(false)
      }
    }
    void init()
  }, [dispatch])

  const shouldInitEngine = mode === ModeEnum.PRIVATE || requestedModeFromUrl === ModeEnum.PRIVATE
  useEffect(() => {
    if (shouldInitEngine) ensureRailgunEngine()
  }, [shouldInitEngine, ensureRailgunEngine])

  const setMode = useCallback((newMode: Mode) => {
    setModeState(newMode)
  }, [])

  const value = useMemo(
    () => ({
      mode,
      railgunEngineInitialized,
      railgunEngineInitializing,
      railgunEngineInitializingError,
      setMode,
      ensureRailgunEngine
    }),
    [
      mode,
      setMode,
      railgunEngineInitialized,
      railgunEngineInitializing,
      railgunEngineInitializingError,
      ensureRailgunEngine
    ]
  )

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>
}

export { AppModeContextProvider, AppModeContext }
